import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Database, Trash2, Download, AlertTriangle, Shield,
  Loader2, CheckCircle, FileJson, FileSpreadsheet, FileCode, Archive,
  RefreshCw, AlertCircle, Lock, Key
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { gestionDatosAPI } from '../api';

const GestionDatos = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entidades, setEntidades] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  const [selectedEntidades, setSelectedEntidades] = useState([]);
  const [backups, setBackups] = useState([]);
  
  // Estados para borrado
  const [borradoStep, setBorradoStep] = useState(0); // 0: selección, 1: código, 2: confirmación
  const [codigoSeguridad, setCodigoSeguridad] = useState('');
  const [codigoIngresado, setCodigoIngresado] = useState('');
  const [confirmacionTexto, setConfirmacionTexto] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [showBorradoDialog, setShowBorradoDialog] = useState(false);
  
  // Estados para exportación
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('json');
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [entidadesRes, backupsRes] = await Promise.all([
        gestionDatosAPI.getEntidades(),
        gestionDatosAPI.getBackups()
      ]);
      setEntidades(entidadesRes.data.entidades || []);
      setBackups(backupsRes.data.backups || []);
      
      // Cargar estadísticas de todas las entidades
      if (entidadesRes.data.entidades?.length > 0) {
        const ids = entidadesRes.data.entidades.map(e => e.id);
        const statsRes = await gestionDatosAPI.getEstadisticas(ids);
        setEstadisticas(statsRes.data.estadisticas || {});
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({ title: "Error cargando datos", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleEntidad = (id) => {
    setSelectedEntidades(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedEntidades.length === entidades.length) {
      setSelectedEntidades([]);
    } else {
      setSelectedEntidades(entidades.map(e => e.id));
    }
  };

  // BORRADO - Paso 1: Solicitar código
  const iniciarBorrado = async () => {
    if (selectedEntidades.length === 0) {
      toast({ title: "Selecciona al menos una entidad", variant: "destructive" });
      return;
    }
    
    setProcesando(true);
    try {
      const response = await gestionDatosAPI.solicitarBorrado(selectedEntidades);
      setCodigoSeguridad(response.data.codigo_seguridad);
      setBorradoStep(1);
      setShowBorradoDialog(true);
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Error al solicitar borrado",
        variant: "destructive" 
      });
    } finally {
      setProcesando(false);
    }
  };

  // BORRADO - Paso 2: Verificar código y mostrar confirmación final
  const verificarCodigo = () => {
    if (codigoIngresado.toUpperCase() !== codigoSeguridad) {
      toast({ title: "Código incorrecto", variant: "destructive" });
      return;
    }
    setBorradoStep(2);
  };

  // BORRADO - Paso 3: Ejecutar borrado
  const ejecutarBorrado = async () => {
    if (confirmacionTexto !== 'CONFIRMAR BORRADO') {
      toast({ title: "Escribe exactamente: CONFIRMAR BORRADO", variant: "destructive" });
      return;
    }
    
    setProcesando(true);
    try {
      const response = await gestionDatosAPI.confirmarBorrado({
        entidades: selectedEntidades,
        confirmacion_texto: confirmacionTexto,
        codigo_seguridad: codigoIngresado
      });
      
      toast({ 
        title: "Borrado completado", 
        description: `Se creó backup en: ${response.data.backup_creado}` 
      });
      
      // Resetear estados
      setShowBorradoDialog(false);
      setBorradoStep(0);
      setCodigoSeguridad('');
      setCodigoIngresado('');
      setConfirmacionTexto('');
      setSelectedEntidades([]);
      
      // Recargar datos
      loadData();
    } catch (error) {
      toast({ 
        title: "Error en borrado", 
        description: error.response?.data?.detail || "Error al ejecutar borrado",
        variant: "destructive" 
      });
    } finally {
      setProcesando(false);
    }
  };

  // EXPORTACIÓN
  const ejecutarExportacion = async () => {
    if (selectedEntidades.length === 0) {
      toast({ title: "Selecciona al menos una entidad", variant: "destructive" });
      return;
    }
    
    setExporting(true);
    try {
      const response = await gestionDatosAPI.exportar(selectedEntidades, exportFormat);
      
      // Crear descarga
      const blob = new Blob([response.data], { 
        type: exportFormat === 'json' ? 'application/json' : 
              exportFormat === 'csv' ? 'application/zip' : 'application/sql'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lexduo_export_${new Date().toISOString().slice(0,10)}.${exportFormat === 'csv' ? 'zip' : exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({ title: "Exportación completada" });
      setShowExportDialog(false);
    } catch (error) {
      toast({ 
        title: "Error en exportación", 
        description: error.response?.data?.detail || "Error al exportar datos",
        variant: "destructive" 
      });
    } finally {
      setExporting(false);
    }
  };

  const cerrarDialogBorrado = () => {
    setShowBorradoDialog(false);
    setBorradoStep(0);
    setCodigoSeguridad('');
    setCodigoIngresado('');
    setConfirmacionTexto('');
  };

  const getTotalRegistros = () => {
    return selectedEntidades.reduce((total, id) => {
      return total + (estadisticas[id]?.registros || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/configuracion')}>
          <ChevronLeft className="w-4 h-4 mr-2" />Volver
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Datos</h1>
          <p className="text-gray-600">Exportar o borrar datos de la base de datos</p>
        </div>
      </div>

      {/* Alerta de seguridad */}
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertTriangle className="w-4 h-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <strong>Atención:</strong> Las operaciones de borrado son irreversibles. 
          Siempre se creará una copia de seguridad antes de cualquier borrado, pero úselo con precaución.
        </AlertDescription>
      </Alert>

      {/* Selección de entidades */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Entidades de Datos
              </CardTitle>
              <CardDescription>Selecciona las entidades sobre las que quieres operar</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={selectAll}>
              {selectedEntidades.length === entidades.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {entidades.map((entidad) => {
              const stats = estadisticas[entidad.id];
              const isSelected = selectedEntidades.includes(entidad.id);
              
              return (
                <div 
                  key={entidad.id}
                  onClick={() => toggleEntidad(entidad.id)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox checked={isSelected} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{entidad.nombre}</span>
                        {entidad.critico && (
                          <Badge variant="destructive" className="text-xs">Crítico</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{entidad.descripcion}</p>
                      <div className="mt-2">
                        <Badge variant="outline">
                          {stats?.registros || 0} registros
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {selectedEntidades.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
              <span className="text-sm text-gray-600">
                <strong>{selectedEntidades.length}</strong> entidades seleccionadas • 
                <strong> {getTotalRegistros()}</strong> registros totales
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Exportar */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Download className="w-5 h-5 text-blue-600" />
              Exportar Datos
            </CardTitle>
            <CardDescription>
              Descarga los datos seleccionados en diferentes formatos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full"
              onClick={() => setShowExportDialog(true)}
              disabled={selectedEntidades.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar seleccionados
            </Button>
          </CardContent>
        </Card>

        {/* Borrar */}
        <Card className="border-l-4 border-l-red-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trash2 className="w-5 h-5 text-red-600" />
              Borrar Datos
            </CardTitle>
            <CardDescription>
              Elimina permanentemente los datos seleccionados (con backup previo)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="destructive"
              className="w-full"
              onClick={iniciarBorrado}
              disabled={selectedEntidades.length === 0 || procesando}
            >
              {procesando ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Iniciar borrado seguro
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Backups recientes */}
      {backups.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5" />
              Backups Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {backups.slice(0, 5).map((backup, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-sm">{backup.nombre}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(backup.fecha).toLocaleString('es-ES')} • {backup.tamano_kb} KB
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Exportación */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exportar Datos</DialogTitle>
            <DialogDescription>
              Selecciona el formato de exportación
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {[
              { id: 'json', icon: FileJson, label: 'JSON', desc: 'Formato universal, fácil de leer' },
              { id: 'csv', icon: FileSpreadsheet, label: 'CSV (ZIP)', desc: 'Compatible con Excel, múltiples archivos' },
              { id: 'sql', icon: FileCode, label: 'SQL', desc: 'Sentencias INSERT para restaurar' }
            ].map((format) => (
              <div 
                key={format.id}
                onClick={() => setExportFormat(format.id)}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  exportFormat === format.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <format.icon className={`w-6 h-6 ${exportFormat === format.id ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">{format.label}</p>
                    <p className="text-sm text-gray-500">{format.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Cancelar</Button>
            <Button onClick={ejecutarExportacion} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Descargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Borrado (Multi-paso) */}
      <Dialog open={showBorradoDialog} onOpenChange={cerrarDialogBorrado}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Borrado Seguro - Paso {borradoStep + 1} de 3
            </DialogTitle>
          </DialogHeader>
          
          {/* Paso 1: Mostrar código */}
          {borradoStep === 1 && (
            <div className="space-y-4 py-4">
              <Alert className="bg-yellow-50 border-yellow-200">
                <Key className="w-4 h-4 text-yellow-600" />
                <AlertDescription className="text-yellow-700">
                  Se ha generado un código de seguridad. Ingrésalo para continuar.
                </AlertDescription>
              </Alert>
              
              <div className="text-center p-4 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-500 mb-2">Código de seguridad:</p>
                <p className="text-3xl font-mono font-bold tracking-widest text-red-600">{codigoSeguridad}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Ingresa el código:</label>
                <Input 
                  value={codigoIngresado}
                  onChange={(e) => setCodigoIngresado(e.target.value.toUpperCase())}
                  placeholder="Código de 6 caracteres"
                  className="mt-1 text-center font-mono text-lg tracking-widest"
                  maxLength={6}
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={cerrarDialogBorrado}>Cancelar</Button>
                <Button variant="destructive" onClick={verificarCodigo}>
                  Verificar código
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {/* Paso 2: Confirmación final */}
          {borradoStep === 2 && (
            <div className="space-y-4 py-4">
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  <strong>¡ÚLTIMA ADVERTENCIA!</strong><br/>
                  Estás a punto de borrar <strong>{getTotalRegistros()} registros</strong> de {selectedEntidades.length} entidades.
                  Se creará un backup antes del borrado.
                </AlertDescription>
              </Alert>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium mb-2">Entidades a borrar:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedEntidades.map(id => (
                    <Badge key={id} variant="destructive">{entidades.find(e => e.id === id)?.nombre}</Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">
                  Escribe <span className="text-red-600 font-bold">CONFIRMAR BORRADO</span> para continuar:
                </label>
                <Input 
                  value={confirmacionTexto}
                  onChange={(e) => setConfirmacionTexto(e.target.value)}
                  placeholder="CONFIRMAR BORRADO"
                  className="mt-1"
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={cerrarDialogBorrado}>Cancelar</Button>
                <Button 
                  variant="destructive" 
                  onClick={ejecutarBorrado}
                  disabled={confirmacionTexto !== 'CONFIRMAR BORRADO' || procesando}
                >
                  {procesando ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Ejecutar borrado
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionDatos;
